// lib/chatClient.ts
import { createClient } from '@supabase/supabase-js';
import type { ChatAttachment } from '@/types/chat-attachments';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'http://127.0.0.1:54321';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

const supabase = createClient(supabaseUrl, supabaseAnonKey);

interface Channel {
    id: string;
    name: string;
    description: string;
    type: 'public' | 'private' | 'announcement' | 'dm';
    created_by: string;
    created_at: string;
    role: 'owner' | 'writer' | 'reader';
    is_muted: boolean;
    is_favorite: boolean;
    unread_count: number;
    unread_mentions_count?: number;
    avatar_url?: string;
    last_message?: {
        content: string;
        sender_name: string;
        created_at: string;
        attachment_type?: 'voice' | 'gif' | 'photo' | 'video' | 'file' | null;
    } | null;
    dm_user?: {
        id: string;
        first_name: string;
        last_name: string;
        avatar_url?: string;
    };
}

interface DMUserProfile {
    id: string;
    first_name: string;
    last_name: string;
    email?: string;
    avatar_url?: string;
}

export interface ForwardedFrom {
    original_message_id: string;
    original_channel_id: string;
    original_channel_name: string;
    original_channel_type: string;
    original_author_name: string;
    original_author_avatar_url?: string;
    original_created_at: string;
}

interface Message {
    id: string;
    content: string;
    attachments?: ChatAttachment[];
    reply_to_message_id?: string;
    thread_parent_id?: string;
    thread_reply_count?: number;
    thread_last_reply_at?: string;
    thread_participant_ids?: string[];
    is_edited: boolean;
    forwarded_from?: ForwardedFrom | null;
    created_at: string;
    updated_at: string;
    user_profile_id: string;
    user_profiles: {
        id: string;
        first_name: string;
        last_name: string;
        avatar_url?: string;
    };
}

export interface MessageReaction {
    emoji: string;
    count: number;
    hasReacted: boolean;
    userProfileIds: string[];
}

export interface ServerReaction {
    emoji: string;
    count: number;
    userProfileIds: string[];
}

interface MessagesResponse {
    messages: Message[];
    reactions: Record<string, ServerReaction[]>;
}

interface ThreadMessagesResponse {
    parentMessage: Message;
    replies: Message[];
    channelId: string;
    reactions: Record<string, ServerReaction[]>;
}

export interface FileStatCategory {
    category: 'documents' | 'photos' | 'movies' | 'audio' | 'other';
    file_count: number;
    total_size: number;
}

export interface ChannelFileStats {
    total_files: number;
    total_size: number;
    total_links: number;
    categories: FileStatCategory[];
}

export type AttachmentCategory = 'documents' | 'photos' | 'movies' | 'audio' | 'other';

export interface ChannelAttachmentRecord {
    attachment: ChatAttachment;
    message_id: string;
    created_at: string;
    sender_id: string;
    first_name: string;
    last_name: string;
    avatar_url: string | null;
}

export interface ChannelLinkRecord {
    url: string;
    message_id: string;
    created_at: string;
    sender_id: string;
    first_name: string;
    last_name: string;
    avatar_url: string | null;
}

// In-flight request deduplication for read-only actions
const inflightRequests = new Map<string, Promise<any>>();
const DEDUP_ACTIONS = new Set([
    'get_channels', 'get_dm_channels', 'get_messages', 'get_thread_messages',
    'get_my_mentions', 'get_total_unread_count', 'get_chat_preferences',
    'search_org_users', 'get_mentionable_users', 'get_channel_file_stats',
    'get_channel_attachments', 'get_channel_links', 'get_pinned_messages',
    'get_messages_around',
]);

export async function callChatFunction(action: string, payload: any = {}) {
    // Deduplicate concurrent identical read requests
    const dedup = DEDUP_ACTIONS.has(action);
    const cacheKey = dedup ? `${action}:${JSON.stringify(payload)}` : '';

    if (dedup) {
        const inflight = inflightRequests.get(cacheKey);
        if (inflight) return inflight;
    }

    const promise = (async () => {
        const { data, error } = await supabase.functions.invoke('chat-management', {
            body: {
                action,
                ...payload
            }
        });

        if (error) {
            throw new Error(error.message || 'Chat operation failed');
        }

        if (!data || (data.error && !data.ok)) {
            throw new Error(data?.error || 'Chat operation failed');
        }

        return data;
    })();

    if (dedup) {
        inflightRequests.set(cacheKey, promise);
        promise.finally(() => inflightRequests.delete(cacheKey));
    }

    return promise;
}

export async function createChannel(
    organizationId: string,
    clerkUserId: string,
    channelName: string,
    channelDescription?: string,
    channelType: 'public' | 'private' | 'announcement' = 'public'
): Promise<Channel> {
    const result = await callChatFunction('create_channel', {
        organizationId,
        clerkUserId,
        channelName,
        channelDescription,
        channelType
    });
    return result.channel;
}

export async function deleteChannel(
    channelId: string,
    clerkUserId: string
): Promise<boolean> {
    await callChatFunction('delete_channel', {
        channelId,
        clerkUserId
    });
    return true;
}

export async function getChannels(
    organizationId: string,
    clerkUserId: string
): Promise<Channel[]> {
    const result = await callChatFunction('get_channels', {
        organizationId,
        clerkUserId
    });
    return result.channels;
}

export async function getMessages(
    channelId: string,
    clerkUserId: string,
    limit = 50,
    offset?: number,
    beforeMessageId?: string
): Promise<MessagesResponse> {
    const result = await callChatFunction('get_messages', {
        channelId,
        clerkUserId,
        limit,
        offset,
        beforeMessageId
    });
    return { messages: result.messages, reactions: result.reactions || {} };
}

export async function addChannelMember(
    channelId: string,
    clerkUserId: string,
    userId: string,
    role: 'owner' | 'writer' | 'reader'
): Promise<any> {
    const result = await callChatFunction('add_member', {
        channelId,
        clerkUserId,
        userId,
        role
    });
    return result.member;
}

export async function toggleChannelMute(
    channelId: string,
    clerkUserId: string,
    isMuted: boolean
): Promise<boolean> {
    const result = await callChatFunction('toggle_mute', {
        channelId,
        clerkUserId,
        isMuted
    });
    return result.is_muted;
}

export async function toggleChannelFavorite(
    channelId: string,
    clerkUserId: string,
    isFavorite: boolean
): Promise<boolean> {
    const result = await callChatFunction('toggle_favorite', {
        channelId,
        clerkUserId,
        isFavorite
    });
    return result.is_favorite;
}

export async function ensureGeneralChannel(
    organizationId: string,
    clerkUserId: string
): Promise<Channel> {
    const result = await callChatFunction('ensure_general_channel', {
        organizationId,
        clerkUserId
    });
    return result.channel;
}

export async function getThreadMessages(
    threadParentId: string,
    clerkUserId: string,
    limit = 100,
    beforeMessageId?: string
): Promise<ThreadMessagesResponse> {
    const result = await callChatFunction('get_thread_messages', {
        threadParentId,
        clerkUserId,
        limit,
        beforeMessageId
    });
    return {
        parentMessage: result.parentMessage,
        replies: result.replies,
        channelId: result.channelId,
        reactions: result.reactions || {}
    };
}

export async function createOrGetDM(
    organizationId: string,
    clerkUserId: string,
    targetUserProfileId: string
): Promise<Channel> {
    const result = await callChatFunction('create_or_get_dm', {
        organizationId,
        clerkUserId,
        targetUserProfileId
    });
    return result.channel;
}

export async function getDMChannels(
    organizationId: string,
    clerkUserId: string
): Promise<Channel[]> {
    const result = await callChatFunction('get_dm_channels', {
        organizationId,
        clerkUserId
    });
    return result.channels;
}

export async function searchOrgUsers(
    organizationId: string,
    clerkUserId: string,
    searchQuery?: string
): Promise<DMUserProfile[]> {
    const result = await callChatFunction('search_org_users', {
        organizationId,
        clerkUserId,
        searchQuery
    });
    return result.users;
}

export async function getTotalUnreadCount(
    organizationId: string,
    clerkUserId: string
): Promise<number> {
    const result = await callChatFunction('get_total_unread_count', {
        organizationId,
        clerkUserId
    });
    return result.totalUnreadCount ?? 0;
}

export async function getChatPreferences(clerkUserId: string): Promise<Record<string, any> | null> {
    const result = await callChatFunction('get_chat_preferences', { clerkUserId });
    return result.preferences || null;
}

export async function saveChatPreferences(clerkUserId: string, preferences: Record<string, any>): Promise<void> {
    await callChatFunction('save_chat_preferences', { clerkUserId, preferences });
}

export async function getChannelFileStats(
    channelId: string,
    clerkUserId: string
): Promise<ChannelFileStats> {
    const result = await callChatFunction('get_channel_file_stats', {
        channelId,
        clerkUserId,
    });
    return result.stats;
}

export async function getChannelAttachments(
    channelId: string,
    category: AttachmentCategory,
    clerkUserId: string,
    limit = 20,
    offset = 0,
    senderId?: string | null
): Promise<ChannelAttachmentRecord[]> {
    const result = await callChatFunction('get_channel_attachments', {
        channelId,
        clerkUserId,
        category,
        limit,
        offset,
        senderId: senderId || null,
    });
    return result.attachments;
}

export async function getChannelLinks(
    channelId: string,
    clerkUserId: string,
    limit = 20,
    offset = 0,
    senderId?: string | null
): Promise<ChannelLinkRecord[]> {
    const result = await callChatFunction('get_channel_links', {
        channelId,
        clerkUserId,
        limit,
        offset,
        senderId: senderId || null,
    });
    return result.links;
}

export interface PinnedMessage {
    id: string;
    message_id: string;
    channel_id: string;
    pinned_by: string;
    pinned_at: string;
    pinner_name: string;
    message: {
        id: string;
        content: string;
        attachments?: ChatAttachment[];
        created_at: string;
        user_profile_id: string;
        user_profiles: {
            id: string;
            first_name: string;
            last_name: string;
            avatar_url?: string;
        };
    } | null;
}

export async function pinMessage(
    messageId: string,
    channelId: string,
    clerkUserId: string
): Promise<void> {
    await callChatFunction('pin_message', { messageId, channelId, clerkUserId });
}

export async function unpinMessage(
    messageId: string,
    channelId: string,
    clerkUserId: string
): Promise<void> {
    await callChatFunction('unpin_message', { messageId, channelId, clerkUserId });
}

export async function getPinnedMessages(
    channelId: string,
    clerkUserId: string,
    limit = 10,
    offset = 0
): Promise<{ messages: PinnedMessage[]; total: number }> {
    const result = await callChatFunction('get_pinned_messages', {
        channelId,
        clerkUserId,
        limit,
        offset,
    });
    return { messages: result.messages, total: result.total };
}

export async function markChannelRead(
    channelId: string,
    clerkUserId: string
): Promise<void> {
    await callChatFunction('mark_channel_read', { channelId, clerkUserId });
}

export interface MessagesAroundResponse {
    messages: Message[];
    reactions: Record<string, ServerReaction[]>;
    targetIndex: number;
    hasMoreBefore: boolean;
    hasMoreAfter: boolean;
}

export async function getMessagesAround(
    messageId: string,
    channelId: string,
    clerkUserId: string,
    limit = 30
): Promise<MessagesAroundResponse> {
    const result = await callChatFunction('get_messages_around', {
        messageId,
        channelId,
        clerkUserId,
        limit,
    });
    return {
        messages: result.messages,
        reactions: result.reactions || {},
        targetIndex: result.targetIndex,
        hasMoreBefore: result.hasMoreBefore,
        hasMoreAfter: result.hasMoreAfter,
    };
}

export async function requestChannelAvatarUpload(
    channelId: string,
    clerkUserId: string,
    fileName: string,
    organizationId?: string
): Promise<{ uploadUrl: string; token: string; storagePath: string }> {
    const result = await callChatFunction('request_channel_avatar_upload', {
        channelId,
        clerkUserId,
        avatarFileName: fileName,
        organizationId,
    });
    return { uploadUrl: result.uploadUrl, token: result.token, storagePath: result.storagePath };
}

export async function updateChannelAvatar(
    channelId: string,
    clerkUserId: string,
    storagePath: string,
    organizationId?: string
): Promise<{ avatarUrl: string }> {
    const result = await callChatFunction('update_channel_avatar', {
        channelId,
        clerkUserId,
        storagePath,
        organizationId,
    });
    return { avatarUrl: result.avatarUrl };
}

export async function removeChannelAvatar(
    channelId: string,
    clerkUserId: string,
    storagePath?: string,
    organizationId?: string
): Promise<void> {
    await callChatFunction('remove_channel_avatar', {
        channelId,
        clerkUserId,
        storagePath,
        organizationId,
    });
}

export async function requestUserAvatarUpload(
    clerkUserId: string,
    fileName: string
): Promise<{ uploadUrl: string; token: string; storagePath: string }> {
    const result = await callChatFunction('request_user_avatar_upload', {
        clerkUserId,
        avatarFileName: fileName,
    });
    return { uploadUrl: result.uploadUrl, token: result.token, storagePath: result.storagePath };
}

export async function updateUserAvatar(
    clerkUserId: string,
    storagePath: string
): Promise<{ avatarUrl: string }> {
    const result = await callChatFunction('update_user_avatar', {
        clerkUserId,
        storagePath,
    });
    return { avatarUrl: result.avatarUrl };
}

export async function removeUserAvatar(
    clerkUserId: string,
    storagePath?: string
): Promise<void> {
    await callChatFunction('remove_user_avatar', {
        clerkUserId,
        storagePath,
    });
}

export interface UserProfile {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    avatar_url: string | null;
    bio: string | null;
    banner_color: string | null;
    banner_url: string | null;
    created_at: string;
}

export async function getUserProfile(
    clerkUserId: string,
    targetUserProfileId: string
): Promise<{ profile: UserProfile; memberChannelIds: string[] }> {
    return await callChatFunction('get_user_profile', {
        clerkUserId,
        targetUserProfileId,
    });
}

export async function updateUserBio(
    clerkUserId: string,
    bio: string
): Promise<void> {
    await callChatFunction('update_user_bio', { clerkUserId, bio });
}

export async function updateUserBanner(
    clerkUserId: string,
    bannerColor?: string | null,
    bannerUrl?: string | null
): Promise<void> {
    await callChatFunction('update_user_banner', { clerkUserId, bannerColor, bannerUrl });
}

export async function requestBannerUpload(
    clerkUserId: string,
    fileName: string
): Promise<{ uploadUrl: string; token: string; storagePath: string }> {
    const result = await callChatFunction('request_banner_upload', {
        clerkUserId,
        bannerFileName: fileName,
    });
    return { uploadUrl: result.uploadUrl, token: result.token, storagePath: result.storagePath };
}

export async function updateUserBannerImage(
    clerkUserId: string,
    storagePath: string
): Promise<{ bannerUrl: string }> {
    const result = await callChatFunction('update_user_banner_image', {
        clerkUserId,
        storagePath,
    });
    return { bannerUrl: result.bannerUrl };
}

export { supabase };
export type { Channel, Message, ThreadMessagesResponse, MessagesResponse, DMUserProfile };